import React, { useState } from 'react'
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { authService } from '../../services/authService'

interface PasswordResetFormProps {
  onBack?: () => void
}

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ onBack }) => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setMessage('')
    setIsSuccess(false)

    if (!email) {
      setError('メールアドレスを入力してください')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('有効なメールアドレスを入力してください')
      return
    }

    setIsLoading(true)

    try {
      const response = await authService.resetPassword(email)

      if (response.success) {
        setIsSuccess(true)
        setMessage('パスワードリセットメールを送信しました。メールをご確認ください。')
        setEmail('') // フォームをリセット
      }
    } catch (error: any) {
      console.error('パスワードリセットエラー:', error)
      const errorMessage = error.response?.data?.error || 'パスワードリセットに失敗しました'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 4,
        maxWidth: 400,
        width: '100%',
        margin: 'auto',
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Typography variant="h4" component="h1" textAlign="center" gutterBottom>
          パスワードリセット
        </Typography>

        <Typography variant="body2" textAlign="center" color="text.secondary" gutterBottom>
          パスワードを忘れた方はこちら
        </Typography>

        <Divider sx={{ my: 2 }} />

        {isSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isSuccess && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              登録されているメールアドレスを入力してください。
              新しいパスワードをメールで送信いたします。
            </Typography>

            <TextField
              label="メールアドレス"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoComplete="email"
              autoFocus
              disabled={isLoading}
              placeholder="example@company.com"
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isLoading}
              sx={{ mt: 2 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'パスワードリセットメールを送信'
              )}
            </Button>
          </>
        )}

        {/* ログインページに戻るボタン */}
        {onBack && (
          <Box sx={{ textAlign: 'center', pt: 2 }}>
            <Button
              variant="text"
              onClick={onBack}
              disabled={isLoading}
              startIcon={<ArrowBack />}
              sx={{ color: 'text.secondary' }}
            >
              ログインページに戻る
            </Button>
          </Box>
        )}

        {isSuccess && (
          <Box sx={{ textAlign: 'center', pt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              メールが届かない場合は、迷惑メールフォルダもご確認ください。
            </Typography>

            <Button variant="text" onClick={onBack} sx={{ mt: 1 }}>
              ログインページに戻る
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  )
}
